import React, { useState, useEffect, useMemo, useRef } from 'react';
import './../../App.css'; // Assuming you have a CSS file for styling
import './WatchPage.css'; // Assuming you have a CSS file for styling
import { useParams, useNavigate } from 'react-router-dom';

// Import logo.svg from the assets folder
import Header from '../../components/Header';
import useScrolled from '../../hooks/useScrolled';
import PlayIcon from '../../assets/ic_play.svg';
import CommentsIcon from '../../assets/ic_comments.svg';
import Footer from '../../components/Footer';
import video from './../../assets/output.mp4';
import AnimeCardComponent from '../../components/animecard/AnimeCardComponent';
import { DetectAdblock } from '@scthakuri/adblock-detector';
import ApiService from '../../services/ApiService';
import { buildCdnUrl, CDN_BASE_URL, normalizeCdnUrl } from '../../services/CdnUtils';
import SeasonComponent from '../../components/SeasonComponents';
import { wait } from '@testing-library/user-event/dist/utils';
import VideoPlayer from '../../components/player/VideoPlayer';
import userEvent from '@testing-library/user-event';
import WatchRoom from '../../components/player/socket/WatchRoom';
import YandexAdComponent from '../../components/YandexAdComponent';
import '../../components/player/socket/WebSocketService';
import { useDetectAdBlock } from "adblock-detect-react";
import { sendSeriesChange } from '../../components/player/socket/WebSocketService';
import AnimixPremiumPopup from '../../components/premium/AnimixPremiumPopup';
import AppPromo from '../../components/AppPromo';
import AdBlockWarn from '../../components/adblock/AdBlockWarn';
import ReviewComponent from '../../components/ReviewComponent';
import {
    getKodikSrc,
    getPreferredPlayerMode,
    PLAYER_MODE_ANIMIX,
    PLAYER_MODE_ANIMIX_KODIK,
    PLAYER_MODE_KODIK,
    resolvePlayerMode,
    savePreferredPlayerMode,
} from '../../components/player/playerSources';
import { findEpisodeSourceById, flattenEpisodeSources, mergeEpisodeSources } from '../../components/player/episodeSources';
import KodikApiService from '../../services/KodikApiService';

const getQualityCode = (quality) => String(quality?.videoQuality || quality?.quality || '')
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, '_');

const isUhdQuality = (quality) => ['QHD', 'ULTRA_HD', '2K', '4K', '1440P', '2160P']
    .includes(getQualityCode(quality));

const isFullHdQuality = (quality) => ['FULL_HD', 'FHD', '1080', '1080P']
    .includes(getQualityCode(quality));

const getAnimixQualityBadge = (qualities) => {
    if (qualities.some(isUhdQuality)) return 'UHD';
    if (qualities.some(isFullHdQuality)) return 'FHD';
    return null;
};

const WatchPage = () => {
    const { id, seriesId } = useParams();
    const scrolled = useScrolled();
    const [anime, setAnime] = useState([]);
    const [seriesData, setSeriesData] = useState(null);
    const [kodikPlayback, setKodikPlayback] = useState(null);
    const [kodikPlayer, setKodikPlayer] = useState(null);
    const [isKodikPlayerChecking, setIsKodikPlayerChecking] = useState(false);
    const [kodikError, setKodikError] = useState('');
    const [series, setSeries] = useState(null);
    const [watchUrl, setWatchUrl] = useState(null);
    const [watchTime, setWatchTime] = useState(0);
    const [savedWatchTime, setSavedWatchTime] = useState(1);
    const [reportTime, setReportTime] = useState(Date.now());
    const [seriesCount, setSeriesCount] = useState(0);
    const [nextSeries, setNextSeries] = useState(null);
    const [timeSet, setTimeSet] = useState(false);
    const [adDetected, setAdDetected] = useState(false);
    const [timeFirst, setTimeFirst] = useState(0);
    const [isAfkPopupVisible, setIsAfkPopupVisible] = useState(false); // State for AFK popup visibility
    const [isAvailable, setIsAvailable] = useState(true);
    const [currentPrivateId, setCurrentPrivateId] = useState("");
    const navigate = useNavigate();
    const adBlockDetected = useDetectAdBlock();
    const [showSafariModal, setShowSafariModal] = useState(false);

    const [showSubscribePopup, setShowSubscribePopup] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(getPreferredPlayerMode);
    const [isPlayerHelpOpen, setIsPlayerHelpOpen] = useState(false);
    const [animixQualityBadge, setAnimixQualityBadge] = useState(null);

    const [comments, setComments] = useState([]);
    const [commentPage, setCommentPage] = useState(0);
    const [hasMoreComments, setHasMoreComments] = useState(true);
    const [newComment, setNewComment] = useState('');
// Определяем именно Safari (не Chrome/Firefox/Edge на iOS)
const isSafariBrowser = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    const isAppleVendor = /Apple Computer, Inc\./.test(vendor);
    const isSafariUA = /Safari/.test(ua) && !/(Chrome|CriOS|EdgiOS|Edg|OPR|Firefox|FxiOS)/.test(ua);
    return isAppleVendor && isSafariUA;
  };

    useEffect(() => {
        const closePlayerHelpOnEscape = (event) => {
            if (event.key === 'Escape') setIsPlayerHelpOpen(false);
        };
        document.addEventListener('keydown', closePlayerHelpOnEscape);
        return () => document.removeEventListener('keydown', closePlayerHelpOnEscape);
    }, []);

    const combinedSeriesData = useMemo(
        () => mergeEpisodeSources(seriesData, kodikPlayback, id),
        [id, kodikPlayback, seriesData]
    );
    const realKodikSrc = getKodikSrc(kodikPlayer, series);
    const kodikSrc = realKodikSrc;
    const kodikAvailabilityNote = useMemo(() => {
        if (!kodikPlayback || !series?.season) return '';
        const episodeNumbers = (Array.isArray(kodikPlayback.items) ? kodikPlayback.items : [])
            .filter((item) => Number(item.season) === Number(series.season))
            .map((item) => Number(item.episode))
            .filter((episode) => Number.isInteger(episode) && episode > 0)
            .sort((left, right) => left - right);
        if (!episodeNumbers.length) return 'В Kodik нет серий этого сезона';
        if (episodeNumbers.length === 1) return `Kodik доступен для серии ${episodeNumbers[0]}`;
        return `Kodik доступен для серий ${episodeNumbers[0]}–${episodeNumbers[episodeNumbers.length - 1]} (${episodeNumbers.length})`;
    }, [kodikPlayback, series?.season]);
    const isKodikChecking = kodikPlayback === null && !kodikError;

    useEffect(() => {
        let cancelled = false;
        setKodikPlayback(null);
        setKodikError('');

        KodikApiService.getPlaybackSources(id)
            .then((payload) => {
                if (!cancelled) setKodikPlayback(payload);
            })
            .catch((error) => {
                if (cancelled) return;
                if (!['ANIME_NOT_FOUND', 'PLAYER_NOT_FOUND', 'MATCH_AMBIGUOUS'].includes(error.code)) {
                    console.error('Error fetching Kodik playback sources:', error);
                }
                setKodikError(error.code || 'KODIK_UNAVAILABLE');
            });

        return () => { cancelled = true; };
    }, [id]);

    useEffect(() => {
        let cancelled = false;
        setKodikPlayer(null);
        setIsKodikPlayerChecking(Boolean(series?.hasKodik));

        if (selectedPlayer === PLAYER_MODE_ANIMIX || !series?.hasKodik) {
            setIsKodikPlayerChecking(false);
            return () => { cancelled = true; };
        }

        const options = series.kodikMovie
            ? { includeStreams: true }
            : { season: series.season, episode: series.seriesNum, includeStreams: true };

        KodikApiService.getPlayer(id, options)
            .then((payload) => {
                if (!cancelled) {
                    setKodikPlayer(payload);
                    setKodikError('');
                    setIsKodikPlayerChecking(false);
                }
            })
            .catch((error) => {
                if (cancelled) return;
                if (!getKodikSrc(series)) setKodikError(error.code || 'KODIK_UNAVAILABLE');
                setIsKodikPlayerChecking(false);
            });

        return () => { cancelled = true; };
    }, [id, selectedPlayer, series]);

    const handleKodikTranslationChange = async (translationId) => {
        if (!series?.hasKodik) return;

        const options = series.kodikMovie
            ? { translationId, includeStreams: true }
            : { season: series.season, episode: series.seriesNum, translationId, includeStreams: true };
        const payload = await KodikApiService.getPlayer(id, options);
        setKodikPlayer(payload);
    };

    useEffect(() => {
        let cancelled = false;
        setAnimixQualityBadge(null);

        if (!series || series.kodikOnly || series.seriesNum == null || series.season == null) {
            return () => { cancelled = true; };
        }

        const fetchAnimixQualities = async () => {
            try {
                const response = await ApiService.getQualities(series.animeId || anime?.id || id, series.seriesNum, series.season);
                const qualities = Array.isArray(response?.data) ? response.data : [];
                if (!cancelled) setAnimixQualityBadge(getAnimixQualityBadge(qualities));
            } catch (error) {
                console.error('Error checking Animix qualities:', error);
                if (!cancelled) setAnimixQualityBadge(null);
            }
        };

        fetchAnimixQualities();
        return () => { cancelled = true; };
    }, [anime?.id, id, series]);

  
    useEffect(() => {
        if (!combinedSeriesData) return;
        let count = 0;
        Object.values(combinedSeriesData).forEach(season => {
            count += season.length;
        });
        setSeriesCount(count);
    }, [combinedSeriesData]);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    useEffect(() => {
        if (isSafariBrowser()) {
          setShowSafariModal(true);
        }
      }, []);

      
    const randomId = Math.floor(Math.random() * 1000000);

    const fetchComments = async (series, clear = false) => {
        try {

            const commentsData = await ApiService.getComments("comments_anime_" + id + "_" + series.seriesNum + "_" + series.season, clear ? 0 : commentPage);
            // console.log(JSON.stringify(commentsData.data))
            if (clear) {
                setCommentPage(1);
            }
            else {

                setCommentPage(prevPage => prevPage + 1);

            }
            if (commentsData.data.length > 0) {
                if (clear) {
                    setComments(commentsData.data);
                }
                else {
                    setComments(prevComments => [...prevComments, ...commentsData.data]);
                }


            } else {
                setHasMoreComments(false);
            }
        } catch (error) {
            console.error('Error fetching comments:', error);
        }
    };

    const loadMoreComments = () => {

        fetchComments(series);
    };

    const handleNewCommentChange = (event) => {
        setNewComment(event.target.value);
    };


    const handleLeaveComment = async () => {
        if (newComment.trim() === '') return;
        try {
            const response = await ApiService.leaveComment(newComment, "comments_anime_" + anime.id + "_" + series.seriesNum + "_" + series.season);
            if (response.type === 'success') {
                setComments([response.data, ...comments]);

                setNewComment('');
            } else {
                console.error('Failed to leave comment:', response.message);
            }
        } catch (error) {
            console.error('Error leaving comment:', error);
        }
    };


    useEffect(() => {
        const handleScroll = () => {
            resetInactivityTimer(); // Reset timer on scroll
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        getSeriesData();
        fetchData();
        setupInactivityTimer(); // Setup the inactivity timer
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearInactivityTimer(); // Clear the timer when the component unmounts
        };
    }, []);

    const fetchedSeriesRef = React.useRef(false);
    const getSeriesData = async () => {
        if (fetchedSeriesRef.current) return;
        fetchedSeriesRef.current = true;
        try {
            const data = await ApiService.getSeries(id);
            setSeriesData(data.type === 'success' && data.data ? data.data : {});
            setNextSeries(null);
            scrollToTop();
        } catch (error) {
            console.error('Error fetching series data:', error);
            setSeriesData({});
        }
    }

    const animixRequestRef = React.useRef(0);
    const kodikFallbackSeriesRef = React.useRef(null);
    const startedWatchSeriesRef = useRef(null);

    const fetchedAnimeRef = React.useRef(false);
    const fetchData = async () => {
        try {
            if (!fetchedAnimeRef.current) {
                fetchedAnimeRef.current = true;
                const animeData = await ApiService.getAnime(id);
                const blacklisted = await ApiService.isBlacklisted(id);
                if (blacklisted.data == true && localStorage.getItem("app") !== "true") {
                    setIsAvailable(false);
                    setSeries(null)
                    setAnime(null)

                    setComments(null);
                    setCommentPage(0)
                    setSeriesData(null)

                }
                if (animeData.type === "success") {
                    setAnime(animeData.data);
                
                    document.title = "Смотреть " + animeData.data.name + " на animix";
                }
                else {
                    setIsAvailable(false);
                }
            }

        } catch (error) {
            console.error('Error fetching anime list:', error);
        }


    };

    const fetchVideo = async (series, requestId) => {

        if (series?.kodikOnly) {
            setWatchUrl(null);
            setCurrentPrivateId('');
            return;
        }

        try {
            const optimalServerData = await ApiService.getOptimalServer(series.privateId);
            if (animixRequestRef.current !== requestId) return;

            if (optimalServerData.data === "no-cache") {
                setWatchUrl(buildCdnUrl(CDN_BASE_URL, `content/directWatch?privateId=${series.privateId}`));
            }
            else {
                const serverUrl = normalizeCdnUrl(optimalServerData.data);
                const animeData = await ApiService.getWatchSession(series.privateId, serverUrl);
                if (animixRequestRef.current !== requestId) return;
                setWatchUrl(buildCdnUrl(serverUrl, `content/watch/${animeData.data}`));
            }
            setCurrentPrivateId(series.privateId);

        } catch (error) {
            console.error('Error fetching anime list:', error);
        }
    };

    // Watch statistics belong to the Animix episode, not to the currently selected
    // playback UI.  Do not start a session for Kodik-only episodes: they have no
    // Animix series id to report against.
    useEffect(() => {
        if (!series?.id || series.kodikOnly || !localStorage.getItem("sessionKey")) return;
        if (startedWatchSeriesRef.current === String(series.id)) return;

        startedWatchSeriesRef.current = String(series.id);
        ApiService.startWatch(series.id)
            .then((startWatchData) => {
                if (startWatchData.type !== "success") console.log('Error starting watch');
            })
            .catch(() => console.log('Error starting watch'));
    }, [series?.id, series?.kodikOnly]);

    useEffect(() => {
        if (!series) return;
        const preferredMode = getPreferredPlayerMode();
        if (isKodikChecking && preferredMode !== PLAYER_MODE_ANIMIX) {
            setSelectedPlayer(preferredMode);
            return;
        }
        kodikFallbackSeriesRef.current = null;
        setSelectedPlayer(resolvePlayerMode(series, preferredMode));
    }, [isKodikChecking, series?.hasKodik, series?.id, series?.kodikOnly]);

    useEffect(() => {
        const requestId = ++animixRequestRef.current;
        const savedMode = getPreferredPlayerMode();
        if (isKodikChecking && savedMode !== PLAYER_MODE_ANIMIX) return undefined;
        const preferredMode = kodikFallbackSeriesRef.current === String(series?.id)
            ? PLAYER_MODE_ANIMIX
            : resolvePlayerMode(series, savedMode);

        if (selectedPlayer !== preferredMode) return undefined;
        if (selectedPlayer !== PLAYER_MODE_ANIMIX || !series || series.kodikOnly) {
            setWatchUrl(null);
            setCurrentPrivateId('');
            return undefined;
        }

        fetchVideo(series, requestId);
        return () => {
            if (animixRequestRef.current === requestId) animixRequestRef.current += 1;
        };
    }, [isKodikChecking, selectedPlayer, series?.id]);

    useEffect(() => {
        if (!series || selectedPlayer === PLAYER_MODE_ANIMIX || isKodikChecking || isKodikPlayerChecking) return;
        const canUseRawKodik = Boolean(realKodikSrc);

        if (selectedPlayer === PLAYER_MODE_KODIK && canUseRawKodik) return;
        if (selectedPlayer === PLAYER_MODE_ANIMIX_KODIK && canUseRawKodik) return;

        if (canUseRawKodik) {
            setSelectedPlayer(PLAYER_MODE_KODIK);
        } else if (!series.kodikOnly) {
            kodikFallbackSeriesRef.current = String(series.id);
            setSelectedPlayer(PLAYER_MODE_ANIMIX);
        }
    }, [isKodikChecking, isKodikPlayerChecking, realKodikSrc, selectedPlayer, series]);

    const handlePlayerModeChange = (mode) => {
        if (!series) return;
        const resolvedMode = resolvePlayerMode(series, mode);
        if (resolvedMode !== mode) return;
        savePreferredPlayerMode(mode);
        setSelectedPlayer(mode);
    };

    // Players the hint inside the video may offer; mirrors the enabled buttons in the selector.
    const availablePlayerModes = [
        ...(series && !series.kodikOnly ? [{ mode: PLAYER_MODE_ANIMIX, label: 'Animix' }] : []),
        ...(realKodikSrc && !isKodikChecking ? [{ mode: PLAYER_MODE_ANIMIX_KODIK, label: 'AnimixLite' }] : []),
        ...(realKodikSrc ? [{ mode: PLAYER_MODE_KODIK, label: 'Kodik' }] : []),
    ];

    const selectedSeriesIdRef = React.useRef(null);
    useEffect(() => {
        const selectedSeries = findEpisodeSourceById(combinedSeriesData, seriesId);
        const orderedSeries = flattenEpisodeSources(combinedSeriesData);
        const selectedIndex = orderedSeries.findIndex((item) => String(item.id) === String(seriesId));
        setNextSeries(selectedIndex >= 0 ? orderedSeries[selectedIndex + 1] || null : null);

        if (!selectedSeries) return;
        const selectionChanged = selectedSeriesIdRef.current !== String(selectedSeries.id);
        selectedSeriesIdRef.current = String(selectedSeries.id);
        setSeries(selectedSeries);

        if (selectionChanged) {
            setSavedWatchTime(1);
            setWatchUrl(null);
            setComments([]);
            fetchComments(selectedSeries, true);
            setTimeSet(false);
        }
    }, [combinedSeriesData, seriesId]);


    useEffect(() => {
        const sendRequest = () => {
            if (Date.now() - reportTime < 25000) return;
            const currentTimeMillis = Date.now();
            setReportTime(currentTimeMillis)
            if (seriesId && !String(seriesId).startsWith('kodik-')) {
                try {
                    if (localStorage.getItem("sessionKey") != null) {
                        ApiService.endWatch(seriesId, parseInt(watchTime), localStorage.getItem("sessionKey"));
                    } else {
                        ApiService.endWatch(seriesId, parseInt(watchTime), "unknown" + randomId);
                    }
                } catch (error) {
                    console.error('Error ending watch session:', error);
                }
            }
        };
        sendRequest();
        return () => { };
    }, [watchTime, seriesId]);

    const handleNextButton = () => {
        if (isAfkPopupVisible) return;
        if (!nextSeries) return;
        navigate(`/watch/${anime.id}/${nextSeries.id}`);
        handleSelectSeries(nextSeries);
        console.log("trying to show an ad")
        showAd()
    }

    const watchHistorySeriesIdRef = React.useRef(-1);
    useEffect(() => {
        const sendRequest = async () => {
            try {
                if (String(seriesId).startsWith('kodik-')) return;
                if (watchHistorySeriesIdRef.current != seriesId) {
                    watchHistorySeriesIdRef.current = seriesId;
                    const oldSeriesId = seriesId;
                    const watchData = await ApiService.getWatchHistory(seriesId);
                    if (watchData.type === "success" && seriesId == oldSeriesId && savedWatchTime == 1 && watchData.data.watchedTimeSec != 0
                        && savedWatchTime != watchData.data.watchedTimeSec) {
                        setSavedWatchTime(watchData.data.watchedTimeSec);
                    }
                }
            } catch (e) {
                console.error('Error fetching watch history:', e);
            }
        };
        sendRequest();
        return () => { };
    }, [seriesId]);

    const updateWatchTime = (time) => {
        setWatchTime(time);
    };

    const handleSelectSeries = (series) => {
        const selectedId = String(series.id);
        selectedSeriesIdRef.current = selectedId;
        scrollToTop();
        setSavedWatchTime(1);
        setWatchUrl(null);
        setSeries(series);
        setComments([])
        fetchComments( series, true);
        setTimeSet(false);
        if (series.kodikOnly) {
            setCurrentPrivateId('');
        } else {
            sendSeriesChange(id, selectedId, localStorage.getItem("nickname"));
        }

    };

    const handleSocketSeriesChange = (message) => {

        if (message.sender == localStorage.getItem("nickname")) return;

        console.log(message.message);
        let content = JSON.parse(message.message);

        const socketSeriesId = content.seriesId;
        const series = findSeriesById(socketSeriesId);
        if (series) {
            navigate(`/watch/${anime.id}/${series.id}`);
            scrollToTop();
            setSavedWatchTime(1);
            setWatchUrl(null);
            setSeries(series);
            fetchComments(series, true);
            setTimeSet(false);
            selectedSeriesIdRef.current = String(series.id);
        } else {
            console.error(`Series with seriesId ${socketSeriesId} not found`);
        }

    };


    function findSeriesById(seriesId) {
        return findEpisodeSourceById(combinedSeriesData, seriesId);
    }

    const setT = (b) => {
        setTimeSet(b)
    };

    useEffect(() => {
        const checkAdBlock = () => {
            if (Date.now() - timeFirst > 10000 && timeFirst != 0) return;
            if (timeFirst == 0) {
                setTimeFirst(Date.now());
            }
            console.log("render " + timeFirst);

            console.log(adBlockDetected + " adblock");
            if (adBlockDetected) {

                setAdDetected(true);
            }
        };

        checkAdBlock();
        return () => {

        };
    }, [adBlockDetected, timeFirst]);




    // useEffect(() => {
    //     const checkAdBlock = () => {
    //         DetectAdblock((detected) => {
    //             if (timeFirst != 0) return;
    //             if (timeFirst == 0) {
    //                 setTimeFirst(Date.now());
    //             }
    //             console.log("render " + timeFirst);

    //             console.log(detected + " adblock");
    //             if (detected) {

    //                 setAdDetected(true);
    //             }

    //         });
    //     };

    //     checkAdBlock();
    //     return () => {

    //     };
    // }, [adBlockDetected, timeFirst]);

    // Inactivity timer setup
    const inactivityTimeLimit = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    let inactivityTimer;

    const setupInactivityTimer = () => {
        document.addEventListener('mousemove', resetInactivityTimer);
        document.addEventListener('keydown', resetInactivityTimer);
        document.addEventListener('scroll', resetInactivityTimer);
        resetInactivityTimer(); // Initial timer setup
    };

    const clearInactivityTimer = () => {
        clearTimeout(inactivityTimer);
        document.removeEventListener('mousemove', resetInactivityTimer);
        document.removeEventListener('keydown', resetInactivityTimer);
        document.removeEventListener('scroll', resetInactivityTimer);
    };

    const resetInactivityTimer = () => {
        clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(showAfkPopup, inactivityTimeLimit);
    };

    const showAfkPopup = () => {
        setIsAfkPopupVisible(true);
    };

    const hideAfkPopup = () => {
        setIsAfkPopupVisible(false);
        resetInactivityTimer(); // Reset timer after closing popup
    };

    const isAdShownRef = React.useRef(false);

    useEffect(() => {
        console.log("trying to show an ad")
        if (!isAdShownRef.current) {
            isAdShownRef.current = true;
            showAd()
        }

    }, []);


    const showAd = () => {

        console.log("Checking if Ya.Context is available:", window.Ya && window.Ya.Context);
        window.yaContextCb = window.yaContextCb || [];
        window.yaContextCb.push(() => {
            console.log("Trying to create ad with Ya.adfoxCode");
            window.Ya.adfoxCode.create({

                containerId: "ety-cloud-player",
                ownerId: "264109",
                params: {
                    p1: "deceb",
                    p2: "jdut",
                },
                onError: () => console.log("Ad Error"),
                onRender: () => console.log("Ad rendered successfully"),
            });
        });

    };

    const API_DOMAIN = 'https://api.whitewhale.help';

    return (
        <div className="app">
            <Header scrolled={scrolled} />
            <div className={`solid-back ${selectedPlayer === PLAYER_MODE_KODIK ? 'solid-back--kodik' : ''}`} >
                <div className='video-container2'>

                    {isAvailable ? <VideoPlayer initialSrc={watchUrl} series={series} onVideoFinished={handleNextButton} updateWatchTime={updateWatchTime} timeSec={savedWatchTime} timeSet={timeSet} setTimeSet={setT} onSeriesChange={handleSocketSeriesChange}
                        adDetected={adDetected} onNextButtonClicked={handleNextButton}
                        initialPrivateId={currentPrivateId}
                        onPremium={() => setShowSubscribePopup(true)}
                        playerMode={selectedPlayer}
                        kodikSrc={kodikSrc}
                        providedKodikStreams={kodikPlayer?.streams}
                        kodikTranslations={kodikPlayer?.translations}
                        kodikTranslation={kodikPlayer?.translation}
                        onKodikTranslationChange={handleKodikTranslationChange}
                        availablePlayerModes={availablePlayerModes}
                        onPlayerModeChange={handlePlayerModeChange}
                    /> : ''}



                    {/* <div className="ad">
                        <div id="ety-cloud-player" ></div>
                    </div> */}
                </div>
            </div>
            <div className='watch-half-con'>
                <div className="watch-half">
                    {localStorage.getItem("sessionKey") == null ? (
                        <div className='ad-banner'>
                            <h2 className='small-text'>Хотите сохранить прогресс?</h2>
                            <h3 className='small-text'>Если войдёте в аккаунт, мы запомним на каком моменте вы остановились, какие серии посмотрели, соберём всю статистику по просмотрам и многое другое!</h3>
                        </div>
                    ) : ""}
                    {localStorage.getItem("subscribed") == true ? "" : <YandexAdComponent blockId="R-A-8131234-4" />}

                    <div className='anime-top-con'>
                        {anime?.id ? (
                            <a href={`/anime/${anime.id}`}>
                                <div className='anime-snippet'>
                                    <img className='anime-snippet-img' src={`${API_DOMAIN}/anime/getThumb/${anime.id}_thumb.png`} alt="" />
                                    <h4 className='small-text'>{anime.name}</h4>
                                </div>
                            </a>
                        ) : (
                            <div className='anime-snippet anime-snippet--loading'>
                                <span className="anime-snippet-img" aria-hidden="true" />
                                <h4 className='small-text'>Загружаем информацию…</h4>
                            </div>
                        )}

                        <div className='watch-top-actions'>
                            <div className='watch-episode-actions'>
                                {localStorage.getItem("subscribed") !== "true" ? <button className='anime-next-series-button' style={{ background: "white", color: "black" }} onClick={() => {
                                    if (localStorage.getItem("sessionKey") == null) { navigate("/login"); return; }
                                    setShowSubscribePopup(true);
                                }}>Отключить рекламу</button> : ""}
                                {nextSeries != null ? <button className='anime-next-series-button' onClick={handleNextButton}>След. серия</button> : ""}
                            </div>
                        </div>
                    </div>

                    <div className='watch-series-header'>
                        {series ? <h1 className='series-title'>{seriesCount === 1 ? anime.name : `${series.seriesNum} серия, ${series.season} сезон`} </h1> : <p>..</p>}
                        <div className='watch-player-toolbar'>
                            <div className='watch-player-selector-row'>
                                <div className='watch-player-selector' role="group" aria-label="Выбор плеера">
                                    <button
                                        type="button"
                                        className={`watch-player-option ${selectedPlayer === PLAYER_MODE_ANIMIX ? 'is-active' : ''}`}
                                        onClick={() => handlePlayerModeChange(PLAYER_MODE_ANIMIX)}
                                        disabled={Boolean(series?.kodikOnly)}
                                        title={series?.kodikOnly ? 'Этой серии нет в Animix' : ''}
                                    >
                                        <span>Animix</span>
                                        {animixQualityBadge ? (
                                            <span className={`watch-player-quality-badge is-${animixQualityBadge.toLowerCase()}`}>
                                                {animixQualityBadge}
                                            </span>
                                        ) : null}
                                    </button>
                                    <button
                                        type="button"
                                        className={`watch-player-option ${selectedPlayer === PLAYER_MODE_ANIMIX_KODIK ? 'is-active' : ''}`}
                                        onClick={() => handlePlayerModeChange(PLAYER_MODE_ANIMIX_KODIK)}
                                        disabled={!realKodikSrc || isKodikChecking}
                                        title={isKodikChecking && !realKodikSrc
                                            ? 'Получаем поток Kodik…'
                                            : !realKodikSrc
                                                ? (kodikAvailabilityNote || 'Плеер Kodik недоступен')
                                                : 'Kodik в плеере Animix'}
                                    >
                                        AnimixLite
                                    </button>
                                    <button
                                        type="button"
                                        className={`watch-player-option ${selectedPlayer === PLAYER_MODE_KODIK ? 'is-active' : ''}`}
                                        onClick={() => handlePlayerModeChange(PLAYER_MODE_KODIK)}
                                        disabled={!realKodikSrc}
                                        title={!realKodikSrc ? (isKodikChecking ? 'Проверяем Kodik…' : kodikAvailabilityNote || 'Плеер Kodik недоступен') : ''}
                                    >
                                        Kodik RAW
                                    </button>
                                </div>
                                <button type="button" className="watch-player-help-button" onClick={() => setIsPlayerHelpOpen(true)} aria-label="О плеерах" title="О плеерах">?</button>
                            </div>
                            <span className='watch-player-note'>
                                {series?.kodikOnly
                                    ? 'Серия доступна только в Kodik'
                                    : realKodikSrc
                                        ? `${selectedPlayer === PLAYER_MODE_ANIMIX_KODIK ? 'AnimixLite' : 'Kodik'} · ${kodikPlayer?.translation?.title || series?.kodikTranslation?.title || 'доступен'}`
                                        : isKodikChecking
                                            ? 'Проверяем Kodik…'
                                            : kodikError
                                            ? 'Kodik для этой серии недоступен'
                                            : kodikAvailabilityNote || 'В Kodik нет этой серии'}
                            </span>
                        </div>
                    </div>

                    {seriesData || kodikPlayback ? <SeasonComponent seriesData={combinedSeriesData} animeData={anime} onSelectAnime={handleSelectSeries} currentId={seriesId} /> : <p>Загружаем серии...</p>}
                    {localStorage.getItem("subscribed") == true ? "" : <YandexAdComponent blockId="R-A-8131234-5" />}

                </div>
                <div className="watch-half-comments">
                    <div className='watch-comments-title'>
                        <img src={CommentsIcon} />
                        <h2>Комментарии</h2>
                    </div>
                    <div className='comment-form'>

                        <textarea
                            value={newComment}
                            maxLength="200"
                            onChange={handleNewCommentChange}
                            placeholder='Оставить комментарий...'
                        />
                        <p className='settings-text-small' style={{ margin: "10px 10px" }}>Оставляя комментарий, Вы
                            обязуетесь соблюдать Правила Сообщества</p>
                        <button type="button" className='ui-btn ui-btn--secondary' onClick={handleLeaveComment}>
                            Оставить комментарий
                        </button>

                    </div>
                    <div className='comment-section'>
                        {comments && 
                              comments.map((comment) => (
                                <ReviewComponent key={comment.id || comment.createdAt} review={comment} isReview={false} navigate={navigate}
                                    deletable={localStorage.getItem("mod") == "true"} />
                            ))
                        }
                      
                        {hasMoreComments && (
                            <button type="button" className="ui-btn ui-btn--secondary" onClick={loadMoreComments}>
                                Загрузить ещё
                            </button>
                        )}
                    </div>



                </div>
            </div>
            {
                isAfkPopupVisible && (
                    <div className="afk-popup">
                        <div className="afk-popup-content">
                            <h2>Вы всё ещё тут?</h2>
                            <p>Вы были неактивны некоторое время.</p>
                            <button onClick={hideAfkPopup}>Я тут</button>
                        </div>
                    </div>
                )
            }

            {
                isAvailable == false ? (
                    <div className="afk-popup">
                        <div className="afk-popup-content">
                            <h2>Этот контент недоступен</h2>
                            <p>Доступ может быть ограничен в связи с запросом правообладателя.</p>
                            <button onClick={() => navigate('/')}>Понятно :(</button>
                        </div>
                    </div>
                ) : ""
            }
            <AppPromo />
            <Footer />
            {isPlayerHelpOpen && (
                <div className="player-help-backdrop" role="presentation" onMouseDown={() => setIsPlayerHelpOpen(false)}>
                    <section className="player-help-dialog" role="dialog" aria-modal="true" aria-labelledby="player-help-title" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="player-help-header">
                            <h2 id="player-help-title">Выбор плеера</h2>
                            <button type="button" className="player-help-close ui-close ui-close--sm ui-close--ghost" onClick={() => setIsPlayerHelpOpen(false)} aria-label="Закрыть">×</button>
                        </div>
                        <div className="player-help-item">
                            <h3>Animix</h3>
                            <p>Основной плеер Animix с максимальным доступным качеством.</p>
                        </div>
                        <div className="player-help-item">
                            <h3>AnimixLite</h3>
                            <p>Интерфейс Animix с контентом Kodik. Подходит для другой озвучки, но качество может быть ниже.</p>
                        </div>
                        <div className="player-help-item">
                            <h3>Kodik RAW</h3>
                            <p>Оригинальный плеер Kodik. Альтернативный вариант просмотра, если другие режимы не подходят.</p>
                        </div>
                    </section>
                </div>
            )}
            {/* <AdBlockWarn/> */}
            {
                showSubscribePopup && (
                    <AnimixPremiumPopup

                        onClose={() => { setShowSubscribePopup(false) }}
                    />
                )
            }

{
  showSafariModal && (
    <div className="afk-popup">
      <div className="afk-popup-content">
        <h2>iOS и Mac</h2>
        <p>В браузерах Safari плеер не поддерживается. </p>
        <p>Скачайте приложение на iPhone или используйте другой браузер на Mac.</p>
        <button onClick={() => setShowSafariModal(false)}>Понятно</button>
        <div style={{display:'flex', gap:8, marginTop:12}}>
          {/* Кнопка закрытия, чтобы не блокировать пользователя навсегда */}
      
          {/* Если захочешь — добавь ссылку на приложение */}
          {/* <a className="button-sec" href="https://example.com/app" target="_blank" rel="noreferrer">Скачать приложение</a> */}
        </div>
      </div>
    </div>
  )
}


        </div >
    );
};

export default WatchPage;
